import { create } from 'zustand'
import {
  queryPrepDashboard,
  type PrepDateBasis,
  type PrepDashboardCard,
  type PrepDashboardSnapshotCard,
  type PrepListItem,
} from '../api/prep'
import usePrepListStore from './prepListStore'
import usePrepUsageStore from './prepUsageStore'
import useShowClockPrepUsageStore from './showClockPrepUsageStore'
import { readPrepDashboardCache, writePrepDashboardCache } from './prepDashboardCache'

export interface PrepDashboardLoadInput {
  userId: number
  stationId: number
  ageMaxDays: number
  dateBasis?: PrepDateBasis
  includeImages: boolean
  cards: PrepDashboardCard[]
}

export interface PrepDashboardUsedSnapshot {
  item: PrepListItem
  previousItem: PrepListItem | null
  myPrepIndex: number
  affectedCards: Array<{
    cardId: string
    itemIndex: number
    decrementTotal: boolean
  }>
}

interface PrepDashboardState {
  userId: number | null
  stationId: number | null
  cardsById: Record<string, PrepDashboardSnapshotCard>
  itemsById: Record<number, PrepListItem>
  myPrepIds: number[]
  loading: boolean
  refreshing: boolean
  error: string | null
  requestId: string | null
  version: number
  loadedFingerprint: string | null
  source: 'none' | 'cache' | 'network'
  cachedAt: number | null
  lastUpdatedAt: number | null
  stale: boolean
  snapshotDirty: boolean
  myPrepDirty: boolean
  loadSnapshot: (input: PrepDashboardLoadInput, options?: { background?: boolean; force?: boolean }) => Promise<void>
  patchMyPrepMembership: (
    itemId: number,
    inMyPrep: boolean,
    item?: PrepListItem,
    orderedIds?: number[],
  ) => void
  replaceMyPrepCardPage: (input: {
    cardId: string
    items: PrepListItem[]
    total: number
    page: number
    perPage: number
  }) => void
  syncMyPrepOrder: (orderedIds: number[]) => void
  optimisticallyMarkUsed: (item: PrepListItem) => PrepDashboardUsedSnapshot
  rollbackMarkUsed: (snapshot: PrepDashboardUsedSnapshot) => void
  markSnapshotDirty: () => void
  markMyPrepDirty: () => void
  reset: () => void
}

let activeController: AbortController | null = null
let activeFingerprint: string | null = null

const initialState = {
  userId: null,
  stationId: null,
  cardsById: {} as Record<string, PrepDashboardSnapshotCard>,
  itemsById: {} as Record<number, PrepListItem>,
  myPrepIds: [] as number[],
  loading: false,
  refreshing: false,
  error: null as string | null,
  requestId: null as string | null,
  version: 0,
  loadedFingerprint: null as string | null,
  source: 'none' as const,
  cachedAt: null as number | null,
  lastUpdatedAt: null as number | null,
  stale: false,
  snapshotDirty: false,
  myPrepDirty: false,
}

const buildFingerprint = (input: PrepDashboardLoadInput): string =>
  JSON.stringify({
    userId: input.userId,
    stationId: input.stationId,
    ageMaxDays: input.ageMaxDays,
    dateBasis: input.dateBasis ?? 'created_at',
    includeImages: input.includeImages,
    cards: input.cards.map((card) => ({
      id: card.id,
      type: card.type,
      categoryId: card.type === 'category' ? card.categoryId : null,
      page: card.page,
      itemsPerPage: card.itemsPerPage,
    })),
  })

const usePrepDashboardStore = create<PrepDashboardState>((set, get) => ({
  ...initialState,
  loadSnapshot: async (input, options = {}) => {
    if (!input.userId || input.userId <= 0 || !input.stationId || input.stationId <= 0 || input.cards.length === 0) {
      return
    }

    const fingerprint = buildFingerprint(input)
    const { background = false, force = false } = options

    if (!force && get().loadedFingerprint === fingerprint && !get().error) {
      return
    }
    if (!force && activeController && activeFingerprint === fingerprint) {
      return
    }

    activeController?.abort()
    const controller = new AbortController()
    activeController = controller
    activeFingerprint = fingerprint

    const identityChanged =
      (get().stationId !== null && get().stationId !== input.stationId) ||
      (get().userId !== null && get().userId !== input.userId)
    const hasData = !identityChanged && Object.keys(get().cardsById).length > 0
    const cached = !background && !hasData
      ? readPrepDashboardCache(input.userId, input.stationId, fingerprint)
      : null

    set((state) => ({
      ...state,
      userId: input.userId,
      stationId: input.stationId,
      ...(identityChanged
        ? {
            cardsById: {},
            itemsById: {},
            myPrepIds: [],
            loadedFingerprint: null,
            source: 'none' as const,
            cachedAt: null,
            lastUpdatedAt: null,
            stale: false,
            snapshotDirty: false,
            myPrepDirty: false,
          }
        : {}),
      loading: !cached && !background && !hasData,
      refreshing: Boolean(cached) || background || hasData,
      error: background && hasData ? state.error : null,
    }))

    if (cached) {
      const cachedCardsById: Record<string, PrepDashboardSnapshotCard> = {}
      cached.response.cards.forEach((card) => {
        if (card?.id) {
          cachedCardsById[card.id] = { ...card, item_ids: Array.isArray(card.item_ids) ? card.item_ids : [] }
        }
      })
      const cachedMyPrepIds = Array.isArray(cached.response.my_prep_ids)
        ? cached.response.my_prep_ids.filter((id) => Number.isFinite(id) && id > 0)
        : []
      const cachedMyPrepIdSet = new Set(cachedMyPrepIds)
      const cachedItemsById: Record<number, PrepListItem> = {}
      cached.response.items.forEach((item) => {
        if (item && Number.isFinite(item.id) && item.id > 0) {
          cachedItemsById[item.id] = {
            ...item,
            is_in_my_prep: cachedMyPrepIdSet.has(item.id),
          }
        }
      })

      usePrepListStore.getState().replaceMyPrepOrder(cachedMyPrepIds, cachedMyPrepIds.length)
      usePrepUsageStore.getState().hydrateFlags(cached.response.usage_flags ?? [], cached.response.usage_date)
      useShowClockPrepUsageStore.getState().hydrateUsage(
        input.stationId,
        cached.response.clock_usage ?? [],
        cached.response.clock_now,
      )

      set((state) => ({
        ...state,
        userId: input.userId,
        stationId: input.stationId,
        cardsById: cachedCardsById,
        itemsById: cachedItemsById,
        myPrepIds: cachedMyPrepIds,
        loading: false,
        refreshing: true,
        error: null,
        requestId: cached.response.request_id ?? null,
        version: state.version + 1,
        loadedFingerprint: fingerprint,
        source: 'cache',
        cachedAt: cached.savedAt,
        lastUpdatedAt: cached.savedAt,
        stale: false,
        myPrepDirty: false,
      }))
    }

    try {
      const response = await queryPrepDashboard(
        {
          station_id: input.stationId,
          age_max_days: input.ageMaxDays,
          date_basis: input.dateBasis ?? 'created_at',
          include_images: input.includeImages,
          cards: input.cards.map((card) => ({
            id: card.id,
            type: card.type,
            page: Math.max(1, Math.floor(card.page || 1)),
            per_page: Math.min(15, Math.max(3, Math.floor(card.itemsPerPage || 5))),
            ...(card.type === 'category' ? { category_id: card.categoryId } : {}),
          })),
        },
        { signal: controller.signal },
      )

      if (controller.signal.aborted || activeController !== controller) {
        return
      }

      const cardsById: Record<string, PrepDashboardSnapshotCard> = {}
      response.cards.forEach((card) => {
        if (card?.id) {
          cardsById[card.id] = {
            ...card,
            item_ids: Array.isArray(card.item_ids)
              ? card.item_ids.filter((id) => Number.isFinite(id) && id > 0)
              : [],
          }
        }
      })

      const myPrepIds = Array.isArray(response.my_prep_ids)
        ? response.my_prep_ids.filter((id) => Number.isFinite(id) && id > 0)
        : []
      const myPrepIdSet = new Set(myPrepIds)
      const itemsById: Record<number, PrepListItem> = {}
      response.items.forEach((item) => {
        if (item && Number.isFinite(item.id) && item.id > 0) {
          itemsById[item.id] = {
            ...item,
            is_in_my_prep: myPrepIdSet.has(item.id),
          }
        }
      })

      usePrepListStore.getState().replaceMyPrepOrder(myPrepIds, myPrepIds.length)
      usePrepUsageStore.getState().hydrateFlags(response.usage_flags ?? [], response.usage_date)
      useShowClockPrepUsageStore.getState().hydrateUsage(
        input.stationId,
        response.clock_usage ?? [],
        response.clock_now,
      )

      const updatedAt = Date.now()
      writePrepDashboardCache(input.userId, input.stationId, fingerprint, response, updatedAt)

      set((state) => ({
        ...state,
        userId: input.userId,
        stationId: input.stationId,
        cardsById,
        itemsById,
        myPrepIds,
        loading: false,
        refreshing: false,
        error: null,
        requestId: response.request_id ?? null,
        version: state.version + 1,
        loadedFingerprint: fingerprint,
        source: 'network',
        cachedAt: null,
        lastUpdatedAt: updatedAt,
        stale: false,
        snapshotDirty: false,
        myPrepDirty: false,
      }))
    } catch (error) {
      if (controller.signal.aborted || activeController !== controller) {
        return
      }
      set((state) => ({
        ...state,
        loading: false,
        refreshing: false,
        error: error instanceof Error ? error.message : 'Unable to load the Prep Dashboard.',
        stale: Object.keys(state.cardsById).length > 0,
      }))
      throw error
    } finally {
      if (activeController === controller) {
        activeController = null
        activeFingerprint = null
      }
    }
  },
  patchMyPrepMembership: (itemId, inMyPrep, item, orderedIds) => {
    set((state) => {
      const currentItem = state.itemsById[itemId]
      const myPrepCards = Object.values(state.cardsById).filter((card) => card.type === 'my_prep')
      const visibleMyPrepIds = myPrepCards.flatMap((card) => card.item_ids)
      const currentHas = state.myPrepIds.includes(itemId) || visibleMyPrepIds.includes(itemId)
      if ((!currentItem || currentItem.is_in_my_prep === inMyPrep) && currentHas === inMyPrep && !item) {
        return state
      }

      const resolvedItem = currentItem ?? item
      const itemsById = resolvedItem
        ? {
            ...state.itemsById,
            [itemId]: { ...resolvedItem, is_in_my_prep: inMyPrep },
          }
        : state.itemsById
      const suppliedOrderedIds = Array.isArray(orderedIds)
        ? Array.from(
            new Set(
              orderedIds.filter((id) => Number.isFinite(id) && id > 0).map((id) => Math.floor(id)),
            ),
          )
        : null
      const mergedKnownIds = Array.from(
        new Set([
          ...(suppliedOrderedIds ?? []),
          ...state.myPrepIds,
          ...visibleMyPrepIds,
        ]),
      )
      const myPrepIds = inMyPrep
        ? mergedKnownIds.includes(itemId)
          ? mergedKnownIds
          : suppliedOrderedIds
            ? [itemId, ...mergedKnownIds]
            : [...mergedKnownIds, itemId]
        : mergedKnownIds.filter((id) => id !== itemId)
      const currentTotal = Math.max(
        state.myPrepIds.length,
        visibleMyPrepIds.length,
        ...myPrepCards.map((card) => Math.max(0, Math.floor(card.total || 0))),
      )
      const nextTotal = currentHas === inMyPrep
        ? currentTotal
        : Math.max(0, currentTotal + (inMyPrep ? 1 : -1))

      const cardsById = Object.fromEntries(
        Object.entries(state.cardsById).map(([cardId, card]) => {
          if (card.type !== 'my_prep') {
            return [cardId, card]
          }
          const perPage = Math.max(1, Math.floor(card.per_page || 1))
          const page = Math.max(1, Math.floor(card.page || 1))
          const offset = (page - 1) * perPage
          return [
            cardId,
            {
              ...card,
              total: nextTotal,
              item_ids: myPrepIds.slice(offset, offset + perPage),
            },
          ]
        }),
      )

      return { ...state, cardsById, itemsById, myPrepIds, version: state.version + 1 }
    })
  },
  replaceMyPrepCardPage: ({ cardId, items, total, page, perPage }) => {
    set((state) => {
      const currentCard = state.cardsById[cardId]
      if (!currentCard || currentCard.type !== 'my_prep') {
        return state
      }

      const normalizedItems = items.filter(
        (item): item is PrepListItem => Boolean(item && Number.isFinite(item.id) && item.id > 0),
      )
      const itemsById = { ...state.itemsById }
      normalizedItems.forEach((item) => {
        itemsById[item.id] = { ...item, is_in_my_prep: true }
      })

      return {
        ...state,
        cardsById: {
          ...state.cardsById,
          [cardId]: {
            ...currentCard,
            page: Math.max(1, Math.floor(page || 1)),
            per_page: Math.max(1, Math.floor(perPage || 1)),
            total: Math.max(0, Math.floor(total || 0)),
            item_ids: normalizedItems.map((item) => item.id),
          },
        },
        itemsById,
        version: state.version + 1,
      }
    })
  },
  syncMyPrepOrder: (orderedIds) => {
    const normalizedIds = Array.from(
      new Set(
        orderedIds
          .filter((id) => Number.isFinite(id) && id > 0)
          .map((id) => Math.floor(id)),
      ),
    )
    const membership = new Set(normalizedIds)

    set((state) => {
      const itemsById = Object.fromEntries(
        Object.entries(state.itemsById).map(([itemId, item]) => [
          itemId,
          { ...item, is_in_my_prep: membership.has(item.id) },
        ]),
      )
      const cardsById = Object.fromEntries(
        Object.entries(state.cardsById).map(([cardId, card]) => {
          if (card.type !== 'my_prep') {
            return [cardId, card]
          }
          const perPage = Math.max(1, Math.floor(card.per_page || 1))
          const page = Math.max(1, Math.floor(card.page || 1))
          const offset = (page - 1) * perPage
          return [
            cardId,
            {
              ...card,
              total: normalizedIds.length,
              item_ids: normalizedIds.slice(offset, offset + perPage),
            },
          ]
        }),
      )

      return {
        ...state,
        cardsById,
        itemsById,
        myPrepIds: normalizedIds,
        myPrepDirty: false,
        version: state.version + 1,
      }
    })
  },
  optimisticallyMarkUsed: (item) => {
    const state = get()
    const categoryIds = new Set<number>()
    if (typeof item.primary_category_id === 'number' && item.primary_category_id > 0) {
      categoryIds.add(Math.floor(item.primary_category_id))
    }
    if (typeof item.primary_category?.id === 'number' && item.primary_category.id > 0) {
      categoryIds.add(Math.floor(item.primary_category.id))
    }
    item.categories?.forEach((category) => {
      if (typeof category?.id === 'number' && category.id > 0) {
        categoryIds.add(Math.floor(category.id))
      }
    })

    const myPrepIndex = state.myPrepIds.indexOf(item.id)
    const affectedCards: PrepDashboardUsedSnapshot['affectedCards'] = []
    const cardsById = { ...state.cardsById }

    Object.entries(state.cardsById).forEach(([cardId, card]) => {
      const itemIndex = card.item_ids.indexOf(item.id)
      const decrementTotal =
        (card.type === 'my_prep' && myPrepIndex >= 0) ||
        (card.type === 'category' &&
          typeof card.category_id === 'number' &&
          categoryIds.has(card.category_id))

      if (itemIndex < 0 && !decrementTotal) {
        return
      }

      affectedCards.push({ cardId, itemIndex, decrementTotal })
      cardsById[cardId] = {
        ...card,
        item_ids: itemIndex >= 0 ? card.item_ids.filter((id) => id !== item.id) : card.item_ids,
        total: decrementTotal ? Math.max(0, card.total - 1) : card.total,
      }
    })

    const previousItem = state.itemsById[item.id] ?? null
    set({
      ...state,
      cardsById,
      itemsById: previousItem
        ? {
            ...state.itemsById,
            [item.id]: { ...previousItem, is_in_my_prep: false },
          }
        : state.itemsById,
      myPrepIds: state.myPrepIds.filter((id) => id !== item.id),
      myPrepDirty: true,
      version: state.version + 1,
    })

    return { item, previousItem, myPrepIndex, affectedCards }
  },
  rollbackMarkUsed: (snapshot) => {
    set((state) => {
      const cardsById = { ...state.cardsById }
      snapshot.affectedCards.forEach(({ cardId, itemIndex, decrementTotal }) => {
        const card = cardsById[cardId]
        if (!card) {
          return
        }
        const itemIds = card.item_ids.includes(snapshot.item.id)
          ? card.item_ids
          : itemIndex >= 0
            ? [
                ...card.item_ids.slice(0, Math.min(itemIndex, card.item_ids.length)),
                snapshot.item.id,
                ...card.item_ids.slice(Math.min(itemIndex, card.item_ids.length)),
              ]
            : card.item_ids
        cardsById[cardId] = {
          ...card,
          item_ids: itemIds,
          total: decrementTotal ? card.total + 1 : card.total,
        }
      })

      const myPrepIds = snapshot.myPrepIndex >= 0 && !state.myPrepIds.includes(snapshot.item.id)
        ? [
            ...state.myPrepIds.slice(0, Math.min(snapshot.myPrepIndex, state.myPrepIds.length)),
            snapshot.item.id,
            ...state.myPrepIds.slice(Math.min(snapshot.myPrepIndex, state.myPrepIds.length)),
          ]
        : state.myPrepIds

      return {
        ...state,
        cardsById,
        itemsById: snapshot.previousItem
          ? { ...state.itemsById, [snapshot.item.id]: snapshot.previousItem }
          : state.itemsById,
        myPrepIds,
        myPrepDirty: true,
        version: state.version + 1,
      }
    })
  },
  markSnapshotDirty: () => set((state) => (state.snapshotDirty ? state : { ...state, snapshotDirty: true })),
  markMyPrepDirty: () => set((state) => (state.myPrepDirty ? state : { ...state, myPrepDirty: true })),
  reset: () => {
    activeController?.abort()
    activeController = null
    activeFingerprint = null
    set({ ...initialState })
  },
}))

export default usePrepDashboardStore
